import React, { useState } from "react"

import { BrowserInfo, detect } from "detect-browser"

import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/solid"

import { Color } from "~tailwindui/types/enums"

import { AlertMessageBox, Button } from "~tailwindui/Basics"
import Modal from "~tailwindui/Modal"

import airbrake from "src/airbrake-notifier"
import classNames from "src/classNames"
import emptyFunction from "src/emptyFunction"

import { gql, useClient, useMutation } from "hooks/urql"

import ModalWithProvidedBody from "components/ModalWithProvidedBody"
import SupportModal from "components/SupportModal"

import DevicePermissionsInstructions from "./DevicePermissionInstructions"
import JoinAsGhost from "./JoinAsGhost"
import SetupChecklist from "./SetupChecklist"
import VideoPreview from "./VideoPreview"

export type JoinCloudroomModalProps = {
  ghost?: boolean
  onJoin: VoidFunction
  sessionId: string
  viewer: {
    id: string
    type: "Admin" | "Teacher" | "Tutor" | "Student"
    phoneNumber?: string
  }
  previouslyJoined: boolean
}

const JoinCloudroomModal: React.FC<JoinCloudroomModalProps> = ({
  ghost = false,
  onJoin,
  sessionId,
  viewer,
  previouslyJoined,
}) => {
  const client = useClient()
  const [mutationResult, runMutation]: any[] = useMutation(
    joinCloudroomMutation,
    client
  )
  const [error, setError] = useState<string | null>()

  const browser = detect()
  const validBrowser =
    browser?.os === "iOS" ? browser?.name === "ios" : browser?.name === "chrome"
  const browserMessage =
    browser?.os === "iOS"
      ? "Please switch to the Safari app."
      : "Please switch to Google Chrome."

  const [setupComplete, setSetupComplete] = useState<boolean>(false)
  const [cameraMessage, setCameraMessage] = useState<string | null>()
  const [micMessage, setMicMessage] = useState<string | null>()
  const [instructionsOpen, setInstructionsOpen] = useState<
    "camera" | "mic" | "browser" | null
  >()

  const supportModalMessage = [
    !validBrowser && browserMessage,
    cameraMessage,
    micMessage,
  ]
    .filter(Boolean)
    .join(", ")

  const handleJoin = () => {
    if (previouslyJoined) onJoin()
    else {
      runMutation({ sessionId, ghost })
        .then(response => {
          if (!response.data) {
            airbrake.notify({
              error: "response.data undefined in JoinCloudroom mutation",
              name: "JoinCloudroom error",
              params: { response },
            })
            setError(
              `Uh-oh, something went wrong. Please refresh. If the problem persists, ${
                viewer.type === "Tutor"
                  ? "contact support"
                  : "call 1-800-211-1986"
              } for assistance.`
            )
            return
          }
          const { failures } = response.data.joinCloudroom
          if (failures.length > 0) {
            setError(failures[0].message)
          } else onJoin()
        })
        .catch(error => {
          airbrake.notify({
            error,
            name: "JoinCloudroomModal",
            params: { sessionId, userId: viewer.id },
          })
          setError(
            `Uh-oh, something went wrong. Please refresh. If the problem persists, ${
              viewer.type === "Tutor"
                ? "contact support"
                : "call 1-800-211-1986"
            } for assistance.`
          )
        })
    }
  }

  return viewer.type === "Admin" || viewer.type === "Student" ? (
    <ModalWithProvidedBody
      hideTrigger
      isOpen
      hideCloseButton
      modalTitle={`${previouslyJoined ? "Re-Join" : "Join"} Cloudroom`}
      modalClassName="bootstrap-modal"
    >
      <div className="modal-body">
        {ghost ? (
          <JoinAsGhost
            validBrowser={validBrowser}
            browserMessage={browserMessage}
            browser={browser as BrowserInfo}
          />
        ) : instructionsOpen ? (
          <DevicePermissionsInstructions
            instructionsOpen={instructionsOpen}
            browser={browser as BrowserInfo}
            userType={viewer.type}
          />
        ) : (
          <>
            <SetupChecklist
              cameraMessage={cameraMessage}
              micMessage={micMessage}
              browserMessage={browserMessage}
              validBrowser={validBrowser}
              setInstructionsOpen={setInstructionsOpen}
              setupComplete={setupComplete}
            />
            <VideoPreview
              setCameraMessage={setCameraMessage}
              setMicMessage={setMicMessage}
              setSetupComplete={setSetupComplete}
            />
          </>
        )}
      </div>
      <div className="modal-footer">
        {instructionsOpen ? (
          <div className="flex justify-between">
            <button
              className="btn solid blue btn-info"
              onClick={() => setInstructionsOpen(null)}
            >
              Back
            </button>
            {viewer.type === "Student" && (
              <SupportModal
                phoneNumber={viewer.phoneNumber}
                sessionId={sessionId}
                userType={viewer.type}
                error={{
                  name: instructionsOpen,
                  message: supportModalMessage,
                }}
              />
            )}
          </div>
        ) : (
          <div className="flex flex-col">
            {error && <AlertMessageBox>{error}</AlertMessageBox>}
            <div className="flex w-full !justify-center">
              <button
                className={classNames(
                  "btn solid",
                  validBrowser && !cameraMessage && !micMessage
                    ? "btn-success green"
                    : "btn-danger red"
                )}
                onClick={handleJoin}
                disabled={(!ghost && !setupComplete) || mutationResult.fetching}
              >
                {previouslyJoined ? "Re-Join" : "Join"} Cloudroom{" "}
                {validBrowser && !cameraMessage && !micMessage ? (
                  <CheckCircleIcon className="h-5 w-5 align-middle" />
                ) : (
                  <ExclamationTriangleIcon className="h-5 w-5 align-middle" />
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </ModalWithProvidedBody>
  ) : (
    <Modal.Dialog hideButton isOpen closeModal={emptyFunction}>
      <Modal.Header hideCloseButton>
        {previouslyJoined ? "Re-Join" : "Join"} Cloudroom
      </Modal.Header>
      <Modal.Body>
        {ghost ? (
          <JoinAsGhost
            validBrowser={validBrowser}
            browserMessage={browserMessage}
            browser={browser as BrowserInfo}
          />
        ) : instructionsOpen ? (
          <DevicePermissionsInstructions
            instructionsOpen={instructionsOpen}
            browser={browser as BrowserInfo}
            userType={viewer.type}
          />
        ) : (
          <>
            <SetupChecklist
              cameraMessage={cameraMessage}
              micMessage={micMessage}
              browserMessage={browserMessage}
              validBrowser={validBrowser}
              setInstructionsOpen={setInstructionsOpen}
              setupComplete={setupComplete}
            />
            <VideoPreview
              setCameraMessage={setCameraMessage}
              setMicMessage={setMicMessage}
              setSetupComplete={setSetupComplete}
            />
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {instructionsOpen ? (
          <div className="flex justify-between">
            <Button text="Back" onClick={() => setInstructionsOpen(null)} />
            {viewer.type === "Teacher" && (
              <SupportModal
                phoneNumber={viewer.phoneNumber}
                sessionId={sessionId}
                userType={viewer.type}
                error={{
                  name: instructionsOpen,
                  message: supportModalMessage,
                }}
              />
            )}
          </div>
        ) : (
          <div>
            {error && <AlertMessageBox>{error}</AlertMessageBox>}
            <div className="flex !justify-center">
              <Button
                color={
                  validBrowser && !cameraMessage && !micMessage
                    ? Color.Green
                    : Color.Red
                }
                onClick={handleJoin}
                disabled={(!ghost && !setupComplete) || mutationResult.fetching}
              >
                {previouslyJoined ? "Re-Join" : "Join"} Cloudroom{" "}
                {validBrowser && !cameraMessage && !micMessage ? (
                  <CheckCircleIcon className="h-6 w-6 align-middle" />
                ) : (
                  <ExclamationTriangleIcon className="h-6 w-6 align-middle" />
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal.Footer>
    </Modal.Dialog>
  )
}

const joinCloudroomMutation = gql`
  mutation ($sessionId: ID!, $ghost: Boolean) {
    joinCloudroom(sessionId: $sessionId, ghost: $ghost) {
      failures {
        message
      }
    }
  }
`

export default JoinCloudroomModal
